import { useEffect, useRef, useState } from "react";

const PINNED_QUERY = "(min-width: 761px) and (prefers-reduced-motion: no-preference)";

/**
 * 纵向滚动驱动横向轨道：区段高度 = 轨道溢出宽度 + 一屏高度，
 * 轨道在置顶舞台内平移。窄屏或减少动效时退回原生横向滚动。
 * 每张卡片会得到 `--offset`（-1 到 1，相对视口中心）与 `--distance`（绝对值），
 * 区段会得到随滚动速度衰减的 `--skew`（角度）。
 * `speed` 为每滚动 1px 横向移动的距离，大于 1 时用户少滚就能看完整条轨道。
 */
export function usePinnedTrack<TSection extends HTMLElement, TTrack extends HTMLElement>(itemCount: number, speed = 1) {
  const sectionRef = useRef<TSection>(null);
  const trackRef = useRef<TTrack>(null);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(PINNED_QUERY);
    const sync = () => setPinned(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;
    const items = Array.from(track.children) as HTMLElement[];
    if (!pinned) {
      section.style.removeProperty("height");
      section.style.removeProperty("--skew");
      track.style.removeProperty("transform");
      items.forEach((item) => {
        item.style.removeProperty("--offset");
        item.style.removeProperty("--distance");
      });
      return;
    }
    // 置顶模式下舞台不应有内部横向滚动，复位可能残留的吸附位置。
    if (track.parentElement) track.parentElement.scrollLeft = 0;
    let frame = 0;
    let overflow = 0;
    let lastX: number | null = null;
    let skew = 0;
    const layout = () => {
      overflow = Math.max(track.scrollWidth - window.innerWidth, 0);
      section.style.height = `${overflow / speed + window.innerHeight}px`;
    };
    const measure = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const distance = Math.max(rect.height - window.innerHeight, 1);
      const progress = Math.min(1, Math.max(0, -rect.top / distance));
      const x = -overflow * progress;
      track.style.transform = `translate3d(${x.toFixed(1)}px, 0, 0)`;
      const target = lastX === null ? 0 : Math.max(-12, Math.min(12, (x - lastX) / 6));
      lastX = x;
      skew += (target - skew) * 0.25;
      if (Math.abs(skew) < 0.05) skew = 0;
      section.style.setProperty("--skew", `${skew.toFixed(2)}deg`);
      const center = window.innerWidth / 2;
      items.forEach((item) => {
        const box = item.getBoundingClientRect();
        const offset = Math.max(-1, Math.min(1, (box.left + box.width / 2 - center) / window.innerWidth));
        item.style.setProperty("--offset", offset.toFixed(3));
        item.style.setProperty("--distance", Math.abs(offset).toFixed(3));
      });
      // 停止滚动后继续逐帧回正斜切。
      if (skew !== 0) schedule();
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    const relayout = () => {
      layout();
      schedule();
    };
    const observer = new ResizeObserver(relayout);
    observer.observe(track);
    relayout();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", relayout);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", relayout);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pinned, itemCount, speed]);

  return { sectionRef, trackRef, pinned };
}
